import dayjs from "dayjs";
import NumberFormatterUtils from "../../utils/numberFormatterUtils";
import i18n from "../../i18n";

/**
 * Standard app date display — "15 Mar 2025" (localised month), matching
 * import-data / settings / etc. Returns "" for empty and the raw value for
 * anything dayjs can't parse.
 */
export const formatDate = (value?: string | null): string => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid()
    ? d.locale(i18n.language).format("DD MMM YYYY")
    : String(value);
};

/** Numeric date "31/12/2025" (DD/MM/YYYY) — used by the MyData tables where
 *  Figma shows numeric dates rather than the app's "31 Dec 2025". */
export const formatDateNumeric = (value?: string | null): string => {
  if (!value) return "";
  const d = dayjs(value);
  return d.isValid() ? d.format("DD/MM/YYYY") : String(value);
};

export const formatNumber = (n: number): string => {
  // formatDynamic always renders 2 decimals; drop a trailing ".00" so whole
  // numbers show as "12,450" instead of "12,450.00" (real decimals stay).
  const s = NumberFormatterUtils.formatDynamic(n);
  return s.endsWith(".00") ? s.slice(0, -3) : s;
};

/** Compact amount without a unit: 24.5B / 850M / 12,000 — for places that
 *  show the "THB" unit on its own (a stat card's unit, a "(THB)" column). */
export const formatCompactAmount = (n: number): string => {
  if (Math.abs(n) >= 1_000_000_000)
    return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (Math.abs(n) >= 1_000_000) return `${Math.round(n / 1_000_000)}M`;
  return formatNumber(n);
};

/** Compact THB formatting: 24.5B THB / 850M THB / 12,000 THB. */
export const formatTHB = (n: number): string => `${formatCompactAmount(n)} THB`;
